import { create } from 'zustand/react';
import { BracketPriceState, Markup } from './BracketPriceState';
import {
  calculateMarkupFromCoverage,
  calculateMarkupFromDiscount,
  calculateMarkupFromSalesPrice,
} from './packageCalculations';

const initState: Pick<BracketPriceState, 'selectedPackageType' | 'newMarkups' | 'editingFields'> = {
  selectedPackageType: 'PRIMARY',
  newMarkups: undefined,
  editingFields: {
    'F-PAK': undefined,
    'D-PAK': undefined,
    'T-PAK': undefined,
  },
};

export const useBracketPriceStore = create<BracketPriceState>((setState, getState) => ({
  ...initState,

  changeSelectedPackage: (selectedPackageType) => setState({ selectedPackageType }),

  getSalesPriceEditState: (packageCalculation) => {
    const currentCostPriceInFpakUnit = packageCalculation?.priceExVat?.costPrice;

    return currentCostPriceInFpakUnit ? 'editable' : 'noCostPrice';
  },

  setNewSalesPrice: (newSalesPrice, packageGtins, fPakPrices, packageClassCode, includeVat) => {
    const currentState = getState();

    switch (packageClassCode) {
      case 'F-PAK': {
        const fPakPackageMarkup: Markup = {
          ...calculateMarkupFromSalesPrice(
            newSalesPrice,
            includeVat,
            includeVat ? fPakPrices.priceIncVat.costPrice : fPakPrices.priceExVat.costPrice,
            packageGtins.fPakGtin
          ),
          editedField: 'price',
        };
        const dPakPackageMarkup = packageGtins.dPakGtin
          ? calculateMarkupFromSalesPrice(
              newSalesPrice,
              includeVat,
              includeVat ? fPakPrices.priceIncVat.costPrice : fPakPrices.priceExVat.costPrice,
              packageGtins.dPakGtin
            )
          : undefined;
        const tPakPackageMarkup = packageGtins.tPakGtin
          ? calculateMarkupFromSalesPrice(
              newSalesPrice,
              includeVat,
              includeVat ? fPakPrices.priceIncVat.costPrice : fPakPrices.priceExVat.costPrice,
              packageGtins.tPakGtin
            )
          : undefined;
        setState({
          newMarkups: {
            'F-PAK': fPakPackageMarkup,
            'D-PAK': dPakPackageMarkup,
            'T-PAK': tPakPackageMarkup,
          },
          editingFields: {
            ...currentState.editingFields,
            'F-PAK': undefined,
          },
        });
        return;
      }
      case 'D-PAK': {
        const newFPak = currentState.newMarkups?.['F-PAK'];
        const dPakPackageMarkup: Markup = {
          ...calculateMarkupFromSalesPrice(
            newSalesPrice,
            includeVat,
            includeVat ? fPakPrices.priceIncVat.costPrice : fPakPrices.priceExVat.costPrice,
            packageGtins.dPakGtin,
            includeVat
              ? newFPak?.salesPriceIncVat ?? fPakPrices.priceIncVat.salesPrice
              : newFPak?.salesPrice ?? fPakPrices.priceExVat.salesPrice
          ),
          editedField: 'price',
        };
        const tPakPackageMarkup = packageGtins.tPakGtin
          ? calculateMarkupFromSalesPrice(
              newSalesPrice,
              includeVat,
              includeVat ? fPakPrices.priceIncVat.costPrice : fPakPrices.priceExVat.costPrice,
              packageGtins.tPakGtin,
              includeVat
                ? newFPak?.salesPriceIncVat ?? fPakPrices.priceIncVat.salesPrice
                : newFPak?.salesPrice ?? fPakPrices.priceExVat.salesPrice
            )
          : undefined;
        setState({
          newMarkups: {
            'F-PAK': currentState.newMarkups?.['F-PAK'],
            'D-PAK': dPakPackageMarkup,
            'T-PAK': tPakPackageMarkup,
          },
          editingFields: {
            ...currentState.editingFields,
            'D-PAK': undefined,
          },
        });
        return;
      }
      case 'T-PAK': {
        const newFPak = currentState.newMarkups?.['F-PAK'];
        const tPakPackageMarkup: Markup = {
          ...calculateMarkupFromSalesPrice(
            newSalesPrice,
            includeVat,
            includeVat ? fPakPrices.priceIncVat.costPrice : fPakPrices.priceExVat.costPrice,
            packageGtins.tPakGtin,
            includeVat
              ? newFPak?.salesPriceIncVat ?? fPakPrices.priceIncVat.salesPrice
              : newFPak?.salesPrice ?? fPakPrices.priceExVat.salesPrice
          ),
          editedField: 'price',
        };
        setState({
          newMarkups: {
            ...currentState.newMarkups,
            'T-PAK': tPakPackageMarkup,
          },
          editingFields: {
            ...currentState.editingFields,
            'T-PAK': undefined,
          },
        });
        return;
      }
    }
  },

  setNewSalesCoverage: (newSalesCoverage, packageGtins, fPakPrices, packageClassCode) => {
    const currentState = getState();

    switch (packageClassCode) {
      case 'F-PAK': {
        const fPakPackageMarkup: Markup = {
          ...calculateMarkupFromCoverage(
            newSalesCoverage,
            fPakPrices.priceIncVat.costPrice,
            fPakPrices.priceExVat.costPrice,
            undefined,
            packageGtins.fPakGtin
          ),
          editedField: 'coverage',
        };
        const dPakPackageMarkup = packageGtins.dPakGtin
          ? calculateMarkupFromCoverage(
              newSalesCoverage,
              fPakPrices.priceIncVat.costPrice,
              fPakPrices.priceExVat.costPrice,
              fPakPackageMarkup.salesPriceIncVat ?? fPakPrices.priceIncVat.salesPrice,
              packageGtins.dPakGtin
            )
          : undefined;
        const tPakPackageMarkup = packageGtins.tPakGtin
          ? calculateMarkupFromCoverage(
              newSalesCoverage,
              fPakPrices.priceIncVat.costPrice,
              fPakPrices.priceExVat.costPrice,
              fPakPackageMarkup.salesPriceIncVat ?? fPakPrices.priceIncVat.salesPrice,
              packageGtins.tPakGtin
            )
          : undefined;
        setState({
          newMarkups: {
            'F-PAK': fPakPackageMarkup,
            'D-PAK': dPakPackageMarkup,
            'T-PAK': tPakPackageMarkup,
          },
          editingFields: {
            ...currentState.editingFields,
            'F-PAK': undefined,
          },
        });
        return;
      }
      case 'D-PAK': {
        const newFPakPrice = currentState.newMarkups?.['F-PAK']?.salesPriceIncVat;
        const dPakPackageMarkup: Markup = {
          ...calculateMarkupFromCoverage(
            newSalesCoverage,
            fPakPrices.priceIncVat.costPrice,
            fPakPrices.priceExVat.costPrice,
            newFPakPrice ?? fPakPrices.priceIncVat.salesPrice,
            packageGtins.dPakGtin
          ),
          editedField: 'coverage',
        };
        const tPakPackageMarkup = packageGtins.fPakGtin
          ? calculateMarkupFromCoverage(
              newSalesCoverage,
              fPakPrices.priceIncVat.costPrice,
              fPakPrices.priceExVat.costPrice,
              newFPakPrice ?? fPakPrices.priceIncVat.salesPrice,
              packageGtins.tPakGtin
            )
          : undefined;
        setState({
          newMarkups: {
            'F-PAK': currentState.newMarkups?.['F-PAK'],
            'D-PAK': dPakPackageMarkup,
            'T-PAK': tPakPackageMarkup,
          },
          editingFields: {
            ...currentState.editingFields,
            'D-PAK': undefined,
          },
        });
        return;
      }
      case 'T-PAK': {
        const newFPakPrice = currentState.newMarkups?.['F-PAK']?.salesPriceIncVat;
        const tPakPackageMarkup: Markup = {
          ...calculateMarkupFromCoverage(
            newSalesCoverage,
            fPakPrices.priceIncVat.costPrice,
            fPakPrices.priceExVat.costPrice,
            newFPakPrice ?? fPakPrices.priceIncVat.salesPrice,
            packageGtins.tPakGtin
          ),
          editedField: 'coverage',
        };
        setState({
          newMarkups: {
            ...currentState.newMarkups,
            'T-PAK': tPakPackageMarkup,
          },
          editingFields: {
            ...currentState.editingFields,
            'T-PAK': undefined,
          },
        });
        return;
      }
    }
  },

  setNewDiscount: (newDiscount, packageGtins, fPakPrices, packageClassCode) => {
    const currentState = getState();

    switch (packageClassCode) {
      case 'F-PAK': {
        throw new Error('Cannot set discount for F-PAK');
      }
      case 'D-PAK': {
        const newFPak = currentState.newMarkups?.['F-PAK'];
        const dPakPackageMarkup: Markup = {
          ...calculateMarkupFromDiscount(
            newDiscount,
            newFPak?.salesPriceIncVat ?? fPakPrices.priceIncVat.salesPrice,
            newFPak?.salesPrice ?? fPakPrices.priceExVat.salesPrice,
            fPakPrices.priceExVat.costPrice,
            packageGtins.dPakGtin
          ),
          editedField: 'discount',
        };
        const tPakPackageMarkup = packageGtins.tPakGtin
          ? calculateMarkupFromDiscount(
              newDiscount,
              newFPak?.salesPriceIncVat ?? fPakPrices.priceIncVat.salesPrice,
              newFPak?.salesPrice ?? fPakPrices.priceExVat.salesPrice,
              fPakPrices.priceExVat.costPrice,
              packageGtins.tPakGtin
            )
          : undefined;
        setState({
          newMarkups: {
            'F-PAK': currentState.newMarkups?.['F-PAK'],
            'D-PAK': dPakPackageMarkup,
            'T-PAK': tPakPackageMarkup,
          },
          editingFields: {
            ...currentState.editingFields,
            'D-PAK': undefined,
          },
        });
        return;
      }
      case 'T-PAK': {
        const newFPak = currentState.newMarkups?.['F-PAK'];
        const tPakPackageMarkup: Markup = {
          ...calculateMarkupFromDiscount(
            newDiscount,
            newFPak?.salesPriceIncVat ?? fPakPrices.priceExVat.salesPrice,
            newFPak?.salesPrice ?? fPakPrices.priceExVat.salesPrice,
            fPakPrices.priceExVat.costPrice,
            packageGtins.tPakGtin
          ),
          editedField: 'discount',
        };
        setState({
          newMarkups: {
            ...currentState.newMarkups,
            'T-PAK': tPakPackageMarkup,
          },
          editingFields: {
            ...currentState.editingFields,
            'T-PAK': undefined,
          },
        });
        return;
      }
    }
  },

  setEditingField: (field, packageClassCode) =>
    setState({
      editingFields: {
        ...getState().editingFields,
        [packageClassCode]: field,
      },
    }),

  resetMarkup: () =>
    setState({
      newMarkups: undefined,
      editingFields: {
        'F-PAK': undefined,
        'D-PAK': undefined,
        'T-PAK': undefined,
      },
    }),

  resetState: () => setState(initState),
}));
